/**
 * Resolves once `window.scrollY` has stopped changing for two consecutive
 * animation frames (or `maxWaitMs` has elapsed, whichever comes first).
 *
 * Used to wait out a `scrollIntoView({ behavior: 'smooth' })` animation
 * before doing anything else that could itself trigger a scroll (e.g.
 * `.focus()`) - starting a second scroll while the first is still animating
 * makes the browser cancel the smooth animation in favor of an instant jump,
 * which can leave the page scrolled far past the intended target.
 */
const DEFAULT_TOP_OFFSET = 16 as const;

function waitForScrollToSettle(maxWaitMs = 1000): Promise<void> {
  return new Promise((resolve) => {
    const start = performance.now();
    let lastY = window.scrollY;
    let stableFrames = 0;

    const check = () => {
      const currentY = window.scrollY;

      if (currentY === lastY) {
        stableFrames += 1;
      } else {
        stableFrames = 0;
        lastY = currentY;
      }

      if (stableFrames >= 2 || performance.now() - start >= maxWaitMs) {
        resolve();
        return;
      }

      requestAnimationFrame(check);
    };

    requestAnimationFrame(check);
  });
}

/**
 * Smoothly scrolls `element` into view, waits for that scroll animation to
 * settle, then focuses it. Focusing is deferred (rather than fired
 * immediately alongside the scroll) so the browser's own focus-triggered
 * scroll-into-view can never interrupt the smooth animation - see
 * `waitForScrollToSettle` for why that matters. `preventScroll` is also
 * passed as a defence-in-depth measure, since by the time focus happens the
 * element should already be in view.
 *
 * `offset` adds extra distance above the element (in pixels) so it doesn't
 * end up flush against the very top of the viewport - e.g. to leave some
 * breathing room, or clear a sticky header. When `offset` is 0, this uses
 * the native `scrollIntoView` directly.
 */
export async function scrollIntoViewThenFocus(
  element: HTMLElement,
  options?: { offset?: number }
): Promise<void> {
  const offset = options?.offset ?? DEFAULT_TOP_OFFSET;

  if (offset && typeof element.getBoundingClientRect === 'function') {
    const targetTop = element.getBoundingClientRect().top + window.scrollY - offset;
    window.scrollTo({ top: targetTop, behavior: 'smooth' });
  } else {
    element.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
  }

  await waitForScrollToSettle();
  element.focus({ preventScroll: true });
}
